import React, { useEffect } from 'react';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import useLockBodyScroll from '@/hooks/useLockBodyScroll';
import useTranslation from '@/hooks/useTranslation';

const ConfirmRevokeInvitationModal = ({
  invitation,
  onConfirm,
  onCancel,
  loading = false,
  errorMessage = '',
}) => {
  useLockBodyScroll();
  const { t } = useTranslation(['employeeDirectory', 'common']);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onCancel]);

  let displayName = t('employeeDirectory:pending.title', 'Pending Invitations');
  if (invitation?.invitedName?.trim()) {
    displayName = invitation.invitedName.trim();
  } else if (invitation?.email?.trim()) {
    displayName = invitation.email.trim();
  }

  let normalizedEmail = '';
  if (invitation?.email?.trim()) {
    const emailValue = invitation.email.trim();
    if (!invitation?.invitedName?.trim() || invitation.invitedName.trim() !== emailValue) {
      normalizedEmail = emailValue;
    }
  }

  let formattedStartDate = '';
  if (invitation?.startDate) {
    try {
      formattedStartDate = new Date(invitation.startDate).toLocaleDateString();
    } catch (error) {
      formattedStartDate = '';
    }
  }

  let formattedExpiration = '';
  if (invitation?.expiresAt) {
    try {
      formattedExpiration = new Date(invitation.expiresAt).toLocaleDateString();
    } catch (error) {
      formattedExpiration = '';
    }
  }

  return (
    <div
      className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={() => {
        if (!loading) {
          onCancel?.();
        }
      }}
    >
      <div
        className="bg-card border border-border rounded-lg shadow-elevation-2 w-full max-w-md"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="p-6 space-y-6">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 rounded-full bg-destructive/10 text-destructive flex items-center justify-center">
              <Icon name="AlertTriangle" size={24} />
            </div>
            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-foreground">
                {t('employeeDirectory:pending.revokeAction', 'Revoke Invitation')}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t('employeeDirectory:pending.confirmRevoke', 'Are you sure you want to revoke this invitation?')}
              </p>
            </div>
          </div>

          <div className="bg-muted/40 border border-border rounded-md p-4 space-y-3">
            <div>
              <p className="text-sm font-medium text-foreground">{displayName}</p>
              {normalizedEmail && <p className="text-xs text-muted-foreground">{normalizedEmail}</p>}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {invitation?.role && (
                <span className="bg-card border border-border/60 text-foreground px-3 py-1 rounded-full">
                  {invitation.role}
                </span>
              )}
              {invitation?.department && (
                <span className="bg-card border border-border/60 text-foreground px-3 py-1 rounded-full">
                  {invitation.department}
                </span>
              )}
              {invitation?.location && (
                <span className="bg-card border border-border/60 text-foreground px-3 py-1 rounded-full">
                  {invitation.location}
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-muted-foreground">
              <div>
                <p className="font-medium text-foreground">
                  {t('employeeDirectory:pending.startDate', 'Start Date')}
                </p>
                <p>{formattedStartDate || '—'}</p>
              </div>
              <div>
                <p className="font-medium text-foreground">
                  {t('employeeDirectory:pending.expires', 'Expires')}
                </p>
                <p>{formattedExpiration || '—'}</p>
              </div>
            </div>
          </div>

          {errorMessage && (
            <div className="bg-error/10 border border-error/20 text-error text-sm rounded-md p-3">
              {errorMessage}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              {t('employeeDirectory:form.actions.cancel', 'Cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={onConfirm}
              loading={loading}
            >
              {t('employeeDirectory:pending.revokeAction', 'Revoke Invitation')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmRevokeInvitationModal;
